import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { LayoutGrid, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import saguaroSilhouette from "@/assets/saguaro-silhouette.png";
import pricklyPearSilhouette from "@/assets/prickly-pear-silhouette.png";

const SectionTitle = ({ eyebrow, title, script }) => (
  <div className={script ? "mb-4" : "mb-10"}>
    <p className="text-primary font-medium text-sm uppercase tracking-widest mb-2">{eyebrow}</p>
    <h2 className={`${script ? "font-script text-6xl md:text-8xl leading-tight" : "font-serif text-4xl md:text-5xl font-semibold tracking-tight"} text-foreground`}>{title}</h2>
  </div>
);

export const About = ({ property }) => {
  const { t } = useLang();
  return (
  <section id="about" className="relative isolate overflow-hidden py-20 md:py-28 bg-background">
    <img
      src={saguaroSilhouette}
      alt=""
      aria-hidden="true"
      className="pointer-events-none absolute -z-10 right-4 md:right-12 top-1/2 -translate-y-1/2 h-[90%] w-auto object-contain"
    />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-5 gap-10 items-center">
      <div className="md:col-span-3">
        <SectionTitle eyebrow={t("Welcome home")} title={t("Your cozy escape awaits")} script />
        <p className="text-muted-foreground text-lg leading-relaxed">{t(property?.description)}</p>
      </div>
      <div className="md:col-span-2 grid grid-cols-2 gap-4">
        {[
          { n: property?.bedrooms, l: "Bedrooms" },
          { n: property?.beds, l: "Beds" },
          { n: property?.bathrooms, l: "Bathrooms" },
          { n: property?.max_guests, l: "Guests" },
        ].map((s) => (
          <div key={s.l} className="bg-accent rounded-2xl p-6 text-center">
            <div className="font-serif text-5xl font-semibold text-primary">{s.n}</div>
            <div className="text-sm text-muted-foreground mt-1">{t(s.l)}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
  );
};

export const Gallery = ({ property }) => {
  const { t } = useLang();
  const images = property?.images || [];
  const [active, setActive] = useState(null); // index of lightbox image
  const [showAll, setShowAll] = useState(false);

  const openLightbox = (i) => setActive(i);
  const closeLightbox = () => setActive(null);
  const next = (e) => { e?.stopPropagation(); setActive((i) => (i + 1) % images.length); };
  const prev = (e) => { e?.stopPropagation(); setActive((i) => (i - 1 + images.length) % images.length); };

  const hero = images.slice(0, 5);

  return (
    <section id="gallery" className="relative isolate overflow-hidden py-20 md:py-28 bg-muted">
      <img
        src={pricklyPearSilhouette}
        alt=""
        aria-hidden="true"
        style={{ left: "max(0px, calc((100vw - 80rem) / 4))" }}
        className="pointer-events-none absolute -z-10 md:-translate-x-1/2 bottom-0 h-[50%] w-auto object-contain"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <SectionTitle eyebrow={t("Take a look inside")} title={t("A place you'll love")} script />
          {images.length > 5 && (
            <button
              data-testid="show-all-photos-btn"
              onClick={() => setShowAll(true)}
              className="inline-flex items-center gap-2 rounded-full border border-foreground/20 bg-card px-6 py-3 text-foreground font-medium transition-all hover:-translate-y-0.5 hover:shadow-md hover:border-primary shrink-0 mb-1"
            >
              <LayoutGrid className="h-4 w-4 text-primary" strokeWidth={1.75} />
              {t("Show all")} {images.length} {t("photos")}
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[150px] md:auto-rows-[220px] gap-3 md:gap-4">
          {hero.map((photo, i) => (
            <button
              key={photo.url || i}
              data-testid={`gallery-img-${i}`}
              onClick={() => openLightbox(i)}
              className={`relative overflow-hidden rounded-2xl group ${
                i === 0 ? "col-span-2 row-span-2" : ""
              }`}
            >
              <img src={photo.url} alt={photo.caption || `View ${i + 1}`} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/10 transition-colors" />
              {photo.caption && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground/75 to-transparent px-3 pt-8 pb-2.5 text-left">
                  <span className="text-white text-xs md:text-sm font-medium drop-shadow">{photo.caption}</span>
                </div>
              )}
              {i === 4 && images.length > 5 && (
                <div
                  onClick={(e) => { e.stopPropagation(); setShowAll(true); }}
                  className="absolute inset-0 bg-foreground/50 flex items-center justify-center text-white font-semibold text-lg"
                >
                  +{images.length - 5} more
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* All photos grid dialog */}
      <Dialog open={showAll} onOpenChange={setShowAll}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto p-6 md:p-8" data-testid="all-photos-dialog">
          <h3 className="font-serif text-2xl font-semibold text-foreground mb-5">{t("All")} {images.length} {t("photos")}</h3>
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-3 space-y-3">
            {images.map((photo, i) => (
              <button
                key={photo.url || i}
                data-testid={`all-photo-${i}`}
                onClick={() => openLightbox(i)}
                className="block w-full overflow-hidden rounded-xl group break-inside-avoid relative"
              >
                <img src={photo.url} alt={photo.caption || `Photo ${i + 1}`} loading="lazy" className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                {photo.caption && (
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground/75 to-transparent px-3 pt-8 pb-2.5 text-left">
                    <span className="text-white text-sm font-medium drop-shadow">{photo.caption}</span>
                  </div>
                )}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Single-image lightbox with prev/next */}
      <Dialog open={active !== null} onOpenChange={closeLightbox}>
        <DialogContent className="max-w-5xl p-0 overflow-hidden bg-transparent border-0 shadow-none" data-testid="lightbox">
          {active !== null && (
            <div className="relative">
              <img src={images[active].url} alt="Full view" className="w-full h-auto max-h-[85vh] object-contain rounded-2xl" />
              {images[active].caption && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground/80 to-transparent px-5 pt-10 pb-4 rounded-b-2xl">
                  <span className="text-white font-medium">{images[active].caption}</span>
                </div>
              )}
              {images.length > 1 && (
                <>
                  <button data-testid="lightbox-prev" onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-background/90 flex items-center justify-center hover:bg-background transition-colors shadow">
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button data-testid="lightbox-next" onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-background/90 flex items-center justify-center hover:bg-background transition-colors shadow">
                    <ChevronRight className="h-5 w-5" />
                  </button>
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-background/90 rounded-full px-3 py-1 text-xs font-medium text-foreground shadow">
                    {active + 1} / {images.length}
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
